import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import { TrainingEnrollment } from '../../types/trainingTypes';
import { parseLocalDateStr } from '../../utils/trainingUtils';
import '../../css/EnrollmentModal.css';

interface EnrollmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    planId: string;
}

const AVATAR_COLORS = [
    '#3b82f6', '#ef4444', '#8b5cf6', '#f59e0b', '#16a34a',
    '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.[0] ?? '?').toUpperCase();
}

function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatEnrollmentDate(dateStr: string): string {
    const d = parseLocalDateStr(dateStr);
    return `Since ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

/**
 * EnrollmentModal — Per-plan enrollment modal for enrolling/unenrolling users.
 *
 * Validates: Requirements 11.1, 11.2, 11.3, 11.4, 11.5, 11.6, 11.7
 */
export const EnrollmentModal = ({ isOpen, onClose, planId }: EnrollmentModalProps) => {
    const { plans, enrollments, enrollUser, unenrollUser } = useSafetyTrainingContext();
    const { allCompanyUsers } = useUserContext();

    const plan = useMemo(() => plans.find(p => p.id === planId), [plans, planId]);

    // Build plan details string (e.g. "MSHA 8-Hour Refresher — 365 days · 480 min")
    const planDetails = useMemo(() => {
        if (!plan) return '';
        return `${plan.name} — ${plan.cycleDuration} days · ${plan.minimumTimeRequired} min`;
    }, [plan]);

    // Current enrollments for this plan
    const planEnrollments = useMemo(
        () => enrollments.filter(e => e.planId === planId),
        [enrollments, planId],
    );

    // Local toggle state: userId -> enrolled boolean
    const [toggleState, setToggleState] = useState<Record<string, boolean>>({});
    const [isSaving, setIsSaving] = useState(false);

    // Initialize toggle state from current enrollments
    useEffect(() => {
        if (!isOpen) return;
        const state: Record<string, boolean> = {};
        allCompanyUsers.forEach(user => {
            state[user.id] = planEnrollments.some(e => e.userId === user.id);
        });
        setToggleState(state);
    }, [isOpen, allCompanyUsers, planEnrollments]);

    const enrolledCount = useMemo(
        () => Object.values(toggleState).filter(Boolean).length,
        [toggleState],
    );

    const totalUsers = allCompanyUsers.length;

    const handleToggle = useCallback((userId: string) => {
        setToggleState(prev => ({ ...prev, [userId]: !prev[userId] }));
    }, []);

    const handleSelectAll = useCallback(() => {
        const newState: Record<string, boolean> = {};
        allCompanyUsers.forEach(user => { newState[user.id] = true; });
        setToggleState(newState);
    }, [allCompanyUsers]);

    const handleSelectNone = useCallback(() => {
        const newState: Record<string, boolean> = {};
        allCompanyUsers.forEach(user => { newState[user.id] = false; });
        setToggleState(newState);
    }, [allCompanyUsers]);

    // Find enrollment record for a user
    const getEnrollment = useCallback(
        (userId: string): TrainingEnrollment | undefined =>
            planEnrollments.find(e => e.userId === userId),
        [planEnrollments],
    );

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            const promises: Promise<unknown>[] = [];

            allCompanyUsers.forEach(user => {
                const wasEnrolled = planEnrollments.some(e => e.userId === user.id);
                const isNowEnrolled = toggleState[user.id] ?? false;

                if (!wasEnrolled && isNowEnrolled) {
                    // Enroll
                    promises.push(enrollUser(user.id, planId));
                } else if (wasEnrolled && !isNowEnrolled) {
                    // Unenroll
                    const enrollment = planEnrollments.find(e => e.userId === user.id);
                    if (enrollment) {
                        promises.push(unenrollUser(enrollment.id));
                    }
                }
            });

            await Promise.all(promises);
            onClose();
        } catch (error) {
            console.error('Failed to save enrollment changes:', error);
        } finally {
            setIsSaving(false);
        }
    }, [allCompanyUsers, planEnrollments, toggleState, enrollUser, unenrollUser, planId, onClose]);

    if (!isOpen || !plan) return null;

    return (
        <div className="enm-overlay" onClick={onClose}>
            <div className="enm-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="enm-header-wrapper">
                    <div className="enm-header">
                        <h3 className="enm-header-title">User Enrollment</h3>
                        <p className="enm-header-details">{planDetails}</p>
                        <p className="enm-note">
                            Users can be enrolled in multiple plans simultaneously. Each plan tracks independently.
                        </p>
                    </div>
                    <button className="enm-header-close" onClick={onClose} aria-label="Close">×</button>
                </div>

                {/* Controls */}
                <div className="enm-controls">
                    <div className="enm-select-toggle">
                        <button className="enm-select-btn" onClick={handleSelectAll}>Select All</button>
                        <span className="enm-select-divider">/</span>
                        <button className="enm-select-btn" onClick={handleSelectNone}>None</button>
                    </div>
                    <span className="enm-enrolled-count">
                        {enrolledCount} of {totalUsers} enrolled
                    </span>
                </div>

                {/* User list */}
                <div className="enm-user-list">
                    {allCompanyUsers.length === 0 ? (
                        <div className="enm-empty">No users found in this company.</div>
                    ) : (
                        allCompanyUsers.map(user => {
                            const enrollment = getEnrollment(user.id);
                            const isEnrolled = toggleState[user.id] ?? false;

                            return (
                                <div key={user.id} className="enm-user-row">
                                    <div
                                        className="enm-avatar"
                                        style={{ background: getAvatarColor(user.name) }}
                                    >
                                        {getInitials(user.name)}
                                    </div>
                                    <div className="enm-user-info">
                                        <p className="enm-user-name">{user.name}</p>
                                        <p className="enm-user-role">{user.role ?? 'Staff'}</p>
                                    </div>
                                    <span className={`enm-enrollment-status${enrollment ? ' enrolled' : ''}`}>
                                        {enrollment
                                            ? formatEnrollmentDate(enrollment.enrolledDate)
                                            : 'Not enrolled'}
                                    </span>
                                    <label className="enm-toggle">
                                        <input
                                            type="checkbox"
                                            checked={isEnrolled}
                                            onChange={() => handleToggle(user.id)}
                                        />
                                        <span className="enm-toggle-slider" />
                                    </label>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="enm-footer">
                    <p className="enm-footer-note">
                        Newly enrolled users will start tracking from today's date.
                    </p>
                    <div className="enm-footer-actions">
                        <button className="enm-cancel-btn" onClick={onClose}>Cancel</button>
                        <button
                            className="enm-save-btn"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving…' : 'Save Enrollment'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
